import { useEffect, useMemo, useState } from "react";

import { createReference } from "@medplum/core";
import { Patient, Practitioner, Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { Drawer, Flex, Select } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import * as Sentry from "@sentry/react";

import { getQuestionnaireResponse } from "@/clients/services/questionnaire-client-form";
import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import {
  BLOOD_TYPE_VALUE_SET_URL,
  BloodTypeDrawerProps,
  bloodTypeFormSchema,
  BloodTypeFormValues,
} from "@/encounters/zod-schemas/blood-type";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { setAccounts } from "@/utils/accounts";
import { getValueSetOptions, joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

export default function BloodTypeDrawer({
  opened,
  onClose,
  clientId,
  encounterId,
  onSuccess,
}: BloodTypeDrawerProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | Patient;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>();
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse>();
  const [valueSetOptions, setValueSetOptions] = useState<Array<{ value: string; label: string }>>(
    [],
  );
  const [initialValue, setInitialValue] = useState<string | null>(null);

  const form = useForm<BloodTypeFormValues>({
    initialValues: { bloodType: undefined },
    validate: zodResolver(bloodTypeFormSchema),
  });

  const formValues = form.values;

  // Reset form when drawer closes
  useEffect(() => {
    if (!opened) {
      form.reset();
      setInitialValue(null);
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Fetch questionnaire and value set options
  useEffect(() => {
    if (!opened) return;

    (async () => {
      try {
        setIsLoading(true);
        // Fetch questionnaire
        const { bundle } = await medplum.searchResources("Questionnaire", {
          url: Questionnaires.BloodType,
          status: "active",
          _count: "1",
        });
        const savedQuestionnaire = bundle?.entry?.[0]?.resource as Questionnaire | undefined;
        if (savedQuestionnaire?.id) {
          setQuestionnaire(savedQuestionnaire);

          // Fetch existing questionnaire response
          const savedQuestionnaireResponse = await getQuestionnaireResponse(
            savedQuestionnaire.id,
            clientId,
            medplum,
          );
          setQuestionnaireResponse(savedQuestionnaireResponse);

          // Get initial value from questionnaire response
          const bloodTypeCode =
            savedQuestionnaireResponse?.item?.[0]?.answer?.[0]?.valueCoding?.code || null;
          setInitialValue(bloodTypeCode);
          form.setFieldValue("bloodType", bloodTypeCode || undefined);
        }

        // Fetch value set options
        const valueSetElements = await getValueSetOptions(medplum, BLOOD_TYPE_VALUE_SET_URL);
        const options = valueSetElements.map((item) => ({
          value: item.code || "",
          label: item.display || item.code || "",
        }));
        setValueSetOptions(options);
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, medplum, clientId]);

  const hasChanges = useMemo(
    () => initialValue !== formValues.bloodType,
    [initialValue, formValues.bloodType],
  );

  const handleSubmit = async (values: BloodTypeFormValues) => {
    if (!questionnaire) return;
    setIsSubmitting(true);

    try {
      const patientReference = { reference: joinReferenceString("Patient", clientId) };
      const profileReference = createReference(profile);

      // Create questionnaire response data
      const formValuesForResponse: Record<string, unknown> = {};
      if (values.bloodType) {
        formValuesForResponse.bloodType = {
          valueCoding: { code: values.bloodType },
        };
      }

      const questionnaireResponseData = getQuestionnaireResponseFromFormValues(
        questionnaire,
        formValuesForResponse,
        patientReference,
        profileReference,
        questionnaireResponse || null,
      );

      // Add encounter reference if provided
      if (encounterId) {
        questionnaireResponseData.encounter = {
          reference: joinReferenceString("Encounter", encounterId),
        };
      }

      // Create or update questionnaire response
      let questionnaireResponseResults: QuestionnaireResponse | undefined;
      if (questionnaireResponse?.id) {
        questionnaireResponseResults = await medplum.updateResource({
          ...questionnaireResponseData,
          id: questionnaireResponse.id,
        });
      } else {
        questionnaireResponseResults = await medplum.createResource(questionnaireResponseData);
      }

      await setAccounts(medplum, questionnaireResponseResults, [], { extractFromPatient: true });

      setInitialValue(values.bloodType || null);
      if (onSuccess) {
        // Pass the blood type value directly (already in "A+", "B-", etc. format)
        await onSuccess(values.bloodType || null);
      }
      onClose();
    } catch (error) {
      showSystemErrorNotification();
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer.Root
      trapFocus
      size={512}
      opened={opened}
      position="right"
      onClose={onClose}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <form onSubmit={form.onSubmit((values, _) => handleSubmit(values))}>
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton aria-label="Close blood type drawer" />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                    Blood Type & RH Factor
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <DisabledReasonButton
                type="submit"
                miw="fit-content"
                loading={isSubmitting}
                disabledReason={hasChanges ? undefined : NO_CHANGES_REASON}
              >
                Save
              </DisabledReasonButton>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Flex gap="lg" direction="column">
              <Select
                label="Blood Type"
                data={valueSetOptions}
                placeholder={isLoading ? "Loading..." : "Select blood type"}
                {...form.getInputProps("bloodType")}
                clearable
                disabled={isLoading || isSubmitting}
              />
            </Flex>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}
